import React, { FC } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { FormField, FullTeamNumberOptions, TrainingTeamAssignee } from '../../models/training.models';
import { palette } from '../../theme';

interface FullTeamNumberFieldComponentProps {
  field: FormField;
  value: Record<number, number | null>; // memberId -> selected number
  onChange: (value: Record<number, number | null>) => void;
  disabled?: boolean;
  teamMembers: TrainingTeamAssignee[];
}

const FullTeamNumberFieldComponent: FC<FullTeamNumberFieldComponentProps> = ({
  field,
  value,
  onChange,
  disabled = false,
  teamMembers,
}) => {
  const options = field.options as FullTeamNumberOptions;
  const min = options?.min || 1;
  const max = options?.max || 15;

  const numbers = Array.from({ length: max - min + 1 }, (_, i) => i + min);

  const handleSelect = (memberId: number, num: number) => {
    if (disabled) return;

    const newValue = { ...value };
    if (newValue[memberId] === num) {
      // Deselect if clicking the same number
      newValue[memberId] = null;
    } else {
      newValue[memberId] = num;
    }
    onChange(newValue);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        {field.name}
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white', overflow: 'auto' }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, minWidth: 'fit-content' }}>
          <Box sx={{ width: 150, flexShrink: 0 }}></Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', flex: 1 }}>
            {numbers.map((num) => (
              <Typography
                key={num}
                variant="caption"
                fontWeight={600}
                sx={{ width: 24, textAlign: 'center', fontSize: '0.7rem' }}
              >
                {num}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Athlete Rows */}
        {teamMembers.map((member) => (
          <Box
            key={member.memberId}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1.5,
              minWidth: 'fit-content',
              '&:last-child': { mb: 0 },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                width: 150,
                flexShrink: 0,
                color: palette.primary.main,
                fontWeight: 500,
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {member.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', flex: 1 }}>
              {numbers.map((num) => {
                const isSelected = value[member.memberId] === num;
                return (
                  <Box
                    key={num}
                    onClick={() => handleSelect(member.memberId, num)}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `2px solid ${palette.primary.main}`,
                      bgcolor: isSelected ? palette.primary.main : 'transparent',
                      cursor: disabled ? 'default' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      transition: 'all 0.2s',
                      flexShrink: 0,
                      '&:hover': disabled
                        ? {}
                        : {
                            transform: 'scale(1.15)',
                            boxShadow: '0 2px 4px rgba(13, 97, 255, 0.3)',
                          },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default FullTeamNumberFieldComponent;
